import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, Handshake, FileText, Megaphone, BarChart3, Globe } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TYPES = [
  { icon: FileText, title: "Sponsored Content", desc: "Native thought-leadership across Lead ITES editorial." },
  { icon: Handshake, title: "Lead-Generation Campaigns", desc: "Co-branded demand programs with qualified handoff." },
  { icon: Megaphone, title: "Advertising", desc: "Premium display and newsletter placements." },
  { icon: BarChart3, title: "Research Sponsorship", desc: "Underwrite surveys, benchmarks and white papers." },
  { icon: Globe, title: "Performance Marketing", desc: "Outcome-based campaigns tied to pipeline." },
];

const TYPE_OPTIONS = TYPES.map((t) => t.title).concat(["Strategic Partnership"]);
const empty = { name: "", email: "", company: "", partnership_type: "", website: "", message: "" };

const Partnerships = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company || !form.partnership_type) {
      toast.error("Please complete name, email, company and partnership type.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/partnerships", form);
      toast.success("Thank you! Our partnerships team will reach out soon.");
      setForm(empty);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="partnerships-page">
      <section className="relative bg-navy text-white overflow-hidden">
        <div className="absolute inset-0 grain opacity-40" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-20">
          <span className="overline-label text-electric">Partnerships</span>
          <h1 className="mt-3 font-display text-4xl lg:text-6xl font-extrabold max-w-3xl leading-tight">
            Reach decision-makers across the Lead ITES network
          </h1>
          <p className="mt-5 text-white/75 max-w-2xl text-lg">
            Sponsored content, lead-generation campaigns, advertising, research sponsorship and
            performance marketing — built for measurable B2B outcomes.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-16 grid lg:grid-cols-2 gap-12">
        <div>
          <h2 className="font-display text-3xl font-extrabold text-navy">Partnership opportunities</h2>
          <div className="mt-8 space-y-4">
            {TYPES.map((t, i) => (
              <motion.div key={t.title}
                initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.06 }}
                className="flex gap-4 bg-white border border-border rounded-xl p-5 card-lift" data-testid={`partnership-type-${i}`}>
                <div className="h-11 w-11 rounded-lg bg-navy flex items-center justify-center shrink-0">
                  <t.icon className="h-5 w-5 text-electric" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-navy">{t.title}</h3>
                  <p className="text-sm text-muted-foreground">{t.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="lg:sticky lg:top-24 h-fit">
          <div className="rounded-2xl border border-border bg-white p-8 shadow-sm">
            <h2 className="font-display text-2xl font-extrabold text-navy">Become a partner</h2>
            <p className="mt-2 text-sm text-muted-foreground">Tell us about your brand and goals.</p>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="partnership-form">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Full name *</Label><Input value={form.name} onChange={set("name")} data-testid="partner-name" /></div>
                <div className="space-y-1.5"><Label>Company *</Label><Input value={form.company} onChange={set("company")} data-testid="partner-company" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Work email *</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="partner-email" /></div>
                <div className="space-y-1.5"><Label>Website</Label><Input value={form.website} onChange={set("website")} data-testid="partner-website" /></div>
              </div>
              <div className="space-y-1.5">
                <Label>Partnership type *</Label>
                <Select value={form.partnership_type} onValueChange={(v) => setForm((f) => ({ ...f, partnership_type: v }))}>
                  <SelectTrigger data-testid="partner-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {TYPE_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Message</Label><Textarea rows={3} value={form.message} onChange={set("message")} data-testid="partner-message" /></div>
              <Button type="submit" disabled={loading} className="btn-electric w-full rounded-full" data-testid="partner-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Inquiry"}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Partnerships;
